import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "mixpanel";

/**
 * Hook to automatically track page views on route changes
 * Add this hook to your App component to track all page views
 */
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    // Extract page name from pathname
    const pageName = getPageName(location.pathname);
    
    trackPageView(pageName, {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [location]);
}

/**
 * Convert pathname to a readable page name
 */
function getPageName(pathname: string): string {
  const pathMap: Record<string, string> = {
    "/": "Home",
    "/auth/login": "Login",
    "/auth/signup": "Signup",
    "/auth/password-entry": "Password Entry",
    "/auth/password-reset": "Password Reset",
    "/onboarding": "Onboarding",
    "/activity": "Activity",
    "/cases": "My Cases",
    "/cases/search": "Add Case",
    "/account": "Account Settings",
    "/account/edit": "Edit Profile",
    "/account/password": "Change Password",
    "/privacy-policy": "Privacy Policy",
    "/terms-of-use": "Terms of Use",
    "/landing": "Landing",
  };

  // Check for exact match first
  if (pathMap[pathname]) {
    return pathMap[pathname];
  }

  // Check for partial matches (for dynamic routes)
  for (const [path, name] of Object.entries(pathMap)) {
    if (pathname.startsWith(path) && path !== "/") {
      return name;
    }
  }

  // Handle admin routes
  if (pathname.startsWith("/admin")) {
    const adminPath = pathname.replace("/admin", "").replace(/^\//, "");
    return `Admin - ${adminPath || "Dashboard"}`;
  }

  // Default: convert path to title case
  return pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ") || "Home";
}
